// All dates are shown and entered in the STUDIO's timezone, never the
// device's: a client travelling or an owner abroad sees studio time.
import { addDays, format, parseISO } from 'date-fns';
import { ru } from 'date-fns/locale';
import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz';

export const todayIn = (tz: string) => formatInTimeZone(new Date(), tz, 'yyyy-MM-dd');

export const addDaysStr = (day: string, n: number) => format(addDays(parseISO(day), n), 'yyyy-MM-dd');

export const fmt = (iso: string | Date, tz: string, pattern: string) =>
  formatInTimeZone(typeof iso === 'string' ? new Date(iso) : iso, tz, pattern, { locale: ru });

/** 'yyyy-MM-dd' + 'HH:mm' in studio tz -> ISO instant. */
export const zonedToIso = (day: string, time: string, tz: string) => fromZonedTime(`${day}T${time}:00`, tz).toISOString();

export const dayLabel = (day: string, tz: string) => {
  const today = todayIn(tz);
  if (day === today) return 'Сегодня';
  if (day === addDaysStr(today, 1)) return 'Завтра';
  return format(parseISO(day), 'EEEEEE, d MMM', { locale: ru });
};

export const longDay = (day: string) => format(parseISO(day), 'EEEE, d MMMM', { locale: ru });
export const shortWeekday = (day: string) => format(parseISO(day), 'EEEEEE', { locale: ru });
export const dayNumber = (day: string) => format(parseISO(day), 'd');
export const monthShort = (day: string) => format(parseISO(day), 'LLL', { locale: ru });

/** Monday of the week containing `day` (studio-local calendar day). */
export const weekStart = (day: string) => {
  const d = parseISO(day);
  const dow = (d.getDay() + 6) % 7;
  return format(addDays(d, -dow), 'yyyy-MM-dd');
};

export const localDayOf = (iso: string, tz: string) => formatInTimeZone(new Date(iso), tz, 'yyyy-MM-dd');
export const localTimeOf = (iso: string, tz: string) => formatInTimeZone(new Date(iso), tz, 'HH:mm');
export const zoned = (iso: string, tz: string) => toZonedTime(new Date(iso), tz);

export function durationLabel(min: number): string {
  if (min >= 1440 && min % 1440 === 0) {
    const d = min / 1440;
    return `${d} ${plural(d, 'день', 'дня', 'дней')}`;
  }
  if (min >= 1440) return `${Math.round((min / 1440) * 10) / 10} дн.`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (!h) return `${m} мин`;
  return m ? `${h} ч ${m} мин` : `${h} ч`;
}

export function plural(n: number, one: string, few: string, many: string) {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return many;
  if (b > 1 && b < 5) return few;
  if (b === 1) return one;
  return many;
}

export function money(amount: number, currency = 'RUB', from = false) {
  const s = new Intl.NumberFormat('ru-RU', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);
  return from ? `от ${s}` : s;
}

export const WEEKDAY_NAMES = ['Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье'];
export const WEEKDAY_SHORT = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

/** Weekly hours grouped for display: "Пн–Пт 09:00–21:00", "Вс выходной". */
export function hoursSummary(rows: { weekday: number; opens: string; closes: string }[]) {
  const byDay = WEEKDAY_SHORT.map((_, i) =>
    rows.filter((r) => r.weekday === i + 1).map((r) => `${r.opens}–${r.closes}`).join(', ') || 'выходной',
  );
  const groups: { from: number; to: number; text: string }[] = [];
  byDay.forEach((text, i) => {
    const last = groups.at(-1);
    if (last && last.text === text && last.to === i - 1) last.to = i;
    else groups.push({ from: i, to: i, text });
  });
  return groups.map((g) => ({
    days: g.from === g.to ? WEEKDAY_SHORT[g.from] : `${WEEKDAY_SHORT[g.from]}–${WEEKDAY_SHORT[g.to]}`,
    text: g.text,
  }));
}

export const isoWeekdayOf = (day: string) => ((parseISO(day).getDay() + 6) % 7) + 1;
